import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SectionHead from './SectionHead';
import Wordmark from './Wordmark';
import EvidenceRing from './EvidenceRing';
import StrengthBars from './StrengthBars';
import CountUp from './CountUp';
import Reveal from './Reveal';
import useInView from './useInView';
import { BP, LANDING_FONT, LC, MAXW, MOTION, PAD_X, SECTION_Y, r } from './landingTokens';

const labelSx = {
  // xs must fit the longest single-word label — "INTERACTIONS" measures 91px
  // at this size/tracking; anything narrower lets the overflow paint under
  // the adjacent caution box (labels can't wrap, it's one word).
  width: { xs: r(94), md: r(108) },
  flex: 'none',
  fontFamily: LANDING_FONT,
  fontSize: r(10.5),
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: LC.inkMuted,
};
const valueSx = { flex: 1, fontFamily: LANDING_FONT, fontSize: r(14.5), fontWeight: 600, color: LC.ink, lineHeight: 1.4 };
const rowSx = { display: 'flex', alignItems: 'center', gap: { xs: r(10), md: r(14) }, py: r(16) };
// Divider between rows lives on the Reveal wrapper (borderTop), so the row itself
// carries no border — lets each row cascade in independently.
const rowRevealSx = (i) => ({ borderTop: i > 0 ? `${r(1)} solid ${LC.borderSoft}` : 'none' });

// Checkmark that stroke-draws in on scroll (delay staggers it a beat after its
// row's reveal), like a verification resolving.
const Check = ({ stroke = LC.posInk, delay = 0 }) => {
  const [ref, inView, instant] = useInView();
  return (
    <Box ref={ref} sx={{ flex: 'none', width: r(22), height: r(22), borderRadius: '50%', background: LC.posBg, display: 'grid', placeItems: 'center' }}>
      <Box component="svg" width={12} height={12} viewBox="0 0 14 14" aria-hidden="true">
        <Box
          component="path"
          d="M3 7.4l2.6 2.6L11 4.6"
          pathLength="1"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{
            strokeDasharray: 1,
            strokeDashoffset: inView ? 0 : 1,
            transition: instant ? 'none' : `stroke-dashoffset ${MOTION.durationSm}ms ${MOTION.ease} ${delay}ms`,
            '@media (prefers-reduced-motion: reduce)': { strokeDashoffset: 0, transition: 'none' },
          }}
        />
      </Box>
    </Box>
  );
};

export default function StandardSection() {
  return (
    <Box component="section" id="standard" sx={{ background: LC.surfaceAlt, borderBottom: `${r(1)} solid ${LC.border}`, display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: MAXW,
          px: PAD_X,
          py: SECTION_Y,
          display: 'grid',
          gridTemplateColumns: '1fr',
          [BP.desktop]: { gridTemplateColumns: '1fr 1fr' },
          gap: 'clamp(1.75rem, 4vw, 3.25rem)',
          alignItems: 'center',
        }}
      >
        <Reveal repeat sx={{ minWidth: 0 }}>
          <SectionHead
            eyebrow="The standard"
            title="Every supplement clears four checks before it joins your stack."
            lead="Interactions, dose, study strength, and the fit for you — four checks on every supplement. Here’s the verdict on one."
            maxWidth={r(520)}
            leadMaxWidth="48ch"
          />
        </Reveal>

        {/* Verdict card — rows cascade in as the evidence "fills in" */}
        <Box sx={{ minWidth: 0, background: LC.surface, border: `${r(1)} solid ${LC.border}`, borderRadius: r(20), boxShadow: '0 1.875rem 3.75rem -1.75rem rgba(12,31,23,.4)', overflow: 'hidden' }}>
          {/* Head */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: r(12), px: r(22), py: r(20), background: LC.evergreen }}>
            <Box>
              <Box sx={{ fontFamily: LANDING_FONT, fontSize: r(10), letterSpacing: '0.14em', color: LC.mint }}>
                <Wordmark size={10} variant="onDark" /> Verdict
              </Box>
              <Box sx={{ fontFamily: LANDING_FONT, fontWeight: 800, fontSize: r(20), letterSpacing: '-0.02em', color: 'primary.contrastText', mt: r(3) }}>
                Magnesium glycinate
              </Box>
            </Box>
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: r(6), flex: 'none',
                background: alpha(LC.mint, 0.18), border: `${r(1)} solid ${alpha(LC.mint, 0.4)}`, color: 'primary.contrastText',
                fontFamily: LANDING_FONT, fontWeight: 800, fontSize: r(12), letterSpacing: '0.02em', px: r(12), py: r(7), borderRadius: r(999), whiteSpace: 'nowrap',
              }}
            >
              <Box component="svg" width={12} height={12} viewBox="0 0 14 14" aria-hidden="true">
                <path d="M3 7.4l2.6 2.6L11 4.6" fill="none" stroke={LC.mint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </Box>
              In your stack
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ px: r(22), pt: r(6), pb: r(18) }}>
            {/* Interactions */}
            <Reveal delay={0} repeat sx={rowRevealSx(0)}>
              <Box sx={{ ...rowSx, alignItems: 'flex-start' }}>
                <Box sx={{ ...labelSx, pt: r(2) }}>Interactions</Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ background: LC.cautionBg, border: `${r(1)} solid ${LC.cautionBorder}`, borderRadius: r(12), p: r(13), display: 'flex', gap: r(11), alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 'none', width: r(9), height: r(9), borderRadius: '50%', background: LC.cautionDot, mt: r(5) }} />
                    <Box sx={{ fontFamily: LANDING_FONT, fontSize: r(13.5), color: LC.cautionInk, lineHeight: 1.5 }}>
                      <Box component="strong" sx={{ fontWeight: 700 }}>1 interaction found — overall risk: low.</Box>{' '}
                      Magnesium can reduce absorption of levothyroxine — separate them by at least 4 hours. Review with your healthcare provider before starting.
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Reveal>

            {/* Dose */}
            <Reveal delay={MOTION.stagger} repeat sx={rowRevealSx(1)}>
              <Box sx={rowSx}>
                <Box sx={labelSx}>Dose</Box>
                <Box sx={valueSx}>200 mg — the amount used in the trial</Box>
                <Check delay={MOTION.stagger + 200} />
              </Box>
            </Reveal>

            {/* Study strength */}
            <Reveal delay={MOTION.stagger * 2} repeat sx={rowRevealSx(2)}>
              <Box sx={rowSx}>
                <Box sx={labelSx}>Study strength</Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ fontFamily: LANDING_FONT, fontSize: r(14.5), fontWeight: 600, color: LC.ink }}>Strong — randomized controlled trial</Box>
                  <Box component="a" href="#" sx={{ display: 'inline-block', mt: r(5), fontFamily: LANDING_FONT, fontWeight: 700, fontSize: r(12.5), color: LC.accent, textDecoration: 'none', '&:hover': { color: LC.linkHover } }}>
                    View 2 studies →
                  </Box>
                </Box>
                <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: r(8) }}>
                  <StrengthBars filled={5} animate />
                  <CountUp value={5} component="b" sx={{ fontFamily: LANDING_FONT, fontWeight: 800, fontSize: r(16), color: LC.evergreen, letterSpacing: '-0.02em' }} />
                </Box>
              </Box>
            </Reveal>

            {/* Relevance */}
            <Reveal delay={MOTION.stagger * 3} repeat sx={rowRevealSx(3)}>
              <Box sx={rowSx}>
                <Box sx={labelSx}>Relevance</Box>
                <Box sx={valueSx}>How well the evidence fits you</Box>
                <EvidenceRing value="4.5" pct={90} size="lg" animate />
              </Box>
            </Reveal>
          </Box>

          {/* Footnote */}
          <Box sx={{ fontFamily: LANDING_FONT, fontSize: r(12), color: LC.inkSoft, px: r(22), py: r(13), borderTop: `${r(1)} solid ${LC.borderSoft}`, background: LC.surfaceAlt, lineHeight: 1.5 }}>
            Every supplement in your stack gets this — or it doesn’t make the cut.{' '}
            <Box component="span" sx={{ color: LC.inkMuted }}>Sample, for illustration.</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
